import { ActionTree } from "vuex";
import { baseNotificationApi } from "@/config/http-notification";
import { IRootState } from "@/store";
import { INotificationState } from "@/store/modules/notification/state";
import { NotificationMutations } from "@/store/modules/notification/mutations";
import { AuthGetters } from "@/store/modules/auth";
import { accountKey, accountSessionMatches } from "@/utils/accountSession";

let countRequestSequence = 0;

export enum NotificationActions {
  FETCH_DATA_PROVIDER = "FETCH_DATA_PROVIDER_NOTIFICATION",
  FETCH_UNREAD_IDS = "FETCH_UNREAD_NOTIFICATION_IDS",
  FETCH_NOTIFICATION_COUNT = "FETCH_NOTIFICATION_COUNT",
  SET_NOTIFICATION_COUNT = "SET_NOTIFICATION_COUNT",
  SET_PROCESS_INFO_NOTIFICATION = "SET_PROCESS_INFO_NOTIFICATION",
  SET_NOTIFICATIONS_READED = "SET_NOTIFICATIONS_READED",
}

export const actions: ActionTree<INotificationState, IRootState> = {
  async [NotificationActions.FETCH_DATA_PROVIDER]({ commit }, params) {
    try {
      const result = await baseNotificationApi.get("/notification/list", {
        params: params,
      });
      commit(NotificationMutations.SET_LAZY_DATA, result.data.data);
    } catch (error) {
      console.log("fetch data provider error: ", error);
    }
  },
  async [NotificationActions.FETCH_UNREAD_IDS]() {
    const result = await baseNotificationApi.get("/notification/unread-ids");
    return result.data.data || [];
  },
  async [NotificationActions.FETCH_NOTIFICATION_COUNT]({
    commit,
    state,
    rootGetters,
  }) {
    const sequence = ++countRequestSequence;
    const revision = state.countRevision;
    const processRevision = state.processInfoRevision;
    const identity = rootGetters[AuthGetters.GET_USER_IDENTITY];
    const owner = accountKey(identity);
    try {
      const result = await baseNotificationApi.get("/notification/count");
      if (
        sequence !== countRequestSequence ||
        owner !== accountKey(rootGetters[AuthGetters.GET_USER_IDENTITY]) ||
        !accountSessionMatches(identity)
      )
        return;
      const count = result.data.data.notificationCount;
      if (!Number.isSafeInteger(count) || count < 0) {
        throw new Error("Invalid notification count response");
      }
      if (revision === state.countRevision) {
        commit(NotificationMutations.CHANGE_NOTIFICATION_COUNT, count);
      }
      if (
        processRevision === state.processInfoRevision &&
        (result.data.data.processInfo == null ||
          Array.isArray(result.data.data.processInfo))
      ) {
        commit(
          NotificationMutations.CHANGE_PROCESS_INFO_NOTIFICATIONS,
          result.data.data.processInfo
        );
      }
    } catch (error) {
      console.log("fetch data provider error: ", error);
    }
  },
  [NotificationActions.SET_NOTIFICATION_COUNT]({ commit }, payload) {
    commit(NotificationMutations.CHANGE_NOTIFICATION_COUNT, payload);
  },
  [NotificationActions.SET_PROCESS_INFO_NOTIFICATION]({ commit }, payload) {
    if (
      payload &&
      Object.prototype.hasOwnProperty.call(payload, "processInfo")
    ) {
      if (payload.processInfo === null || Array.isArray(payload.processInfo)) {
        commit(
          NotificationMutations.CHANGE_PROCESS_INFO_NOTIFICATIONS,
          payload.processInfo
        );
      }
      return;
    }
    if (
      payload?.templateId &&
      ["INCREASE", "DECREASE"].includes(payload.changeType)
    ) {
      commit(NotificationMutations.CHANGE_PROCESS_INFO_NOTIFICATION, payload);
    }
  },
  async [NotificationActions.SET_NOTIFICATIONS_READED]({ dispatch }, payload) {
    try {
      await baseNotificationApi.post(`/notification/readed-notifications`, {
        notificationIds: payload.notificationIds,
      });
    } catch (error) {
      throw error.response;
    }
    await dispatch(NotificationActions.FETCH_NOTIFICATION_COUNT);
    return true;
  },
};
